
import { Question } from "../../types/assessment";
import { createGenericOptions } from "../utils";

export const dataQuestions: Question[] = [
  {
    id: "d1",
    text: "How structured and accessible is your data for AI applications?",
    dimension: "data",
    tooltip: "Evaluate the organization and accessibility of your data across systems. Consider data silos, structured vs. unstructured data, and whether data can be easily accessed by AI systems.",
    options: [
      {
        value: 0,
        label: "Data is completely unstructured, siloed, and inaccessible across systems",
        description: "Data exists in isolated silos with incompatible formats and no common access methods. AI systems cannot effectively utilize organizational data."
      },
      {
        value: 1,
        label: "Basic data organization with 25% structured; significant silos remain with limited accessibility",
        description: "Some structured data exists but remains largely siloed. Limited access mechanisms with minimal standardization across systems."
      },
      {
        value: 2,
        label: "Moderate structure with 50% organized data and emerging governance framework",
        description: "Half of relevant data is structured with an emerging central repository. Basic data dictionaries exist but integration remains challenging."
      },
      {
        value: 3,
        label: "Well-structured data (75%) with centralized access and standardized formats",
        description: "Majority of data follows consistent schemas with established access protocols. Central data warehouse exists with regular synchronization."
      },
      {
        value: 4,
        label: "Highly structured (90%) with semantic layering and advanced data catalogs",
        description: "Nearly all data is well-structured with semantic metadata layers. Comprehensive data catalogs enable discovery and access across the organization."
      },
      {
        value: 5,
        label: "Fully optimized data ecosystem with real-time streaming and automated governance",
        description: "Complete data ecosystem with real-time integration, automated governance, and seamless accessibility. Data is immediately available to AI systems with appropriate controls."
      }
    ]
  },
  {
    id: "d2",
    text: "What level of data quality management exists?",
    dimension: "data",
    tooltip: "Assess your processes for ensuring data accuracy, completeness, and reliability. Consider formalized quality controls, monitoring systems, and how consistently they are applied across your data assets.",
    options: [
      {
        value: 0,
        label: "No formal data quality processes; quality issues untracked",
        description: "No systematic approach to data quality exists. Quality issues go undetected and unaddressed with no formal monitoring or remediation processes."
      },
      {
        value: 1,
        label: "Basic data cleaning with manual processes covering 20% of critical data",
        description: "Minimal quality checks performed manually for a small subset of data. Reactive approach to quality issues without standardized procedures."
      },
      {
        value: 2,
        label: "Semi-automated quality controls for 50% of data with validation rules",
        description: "Basic validation rules applied to about half of key data assets. Quality issues identified through periodic reviews with inconsistent remediation."
      },
      {
        value: 3,
        label: "Systematic quality management across 75% of data assets",
        description: "Formalized quality management covers most data assets. Regular monitoring with established data quality metrics and improvement processes."
      },
      {
        value: 4,
        label: "Advanced quality framework covering 90% of data with automated monitoring",
        description: "Comprehensive quality framework with automated detection of anomalies. Nearly all data undergoes continuous quality assessment with alerting mechanisms."
      },
      {
        value: 5,
        label: "Comprehensive quality system with ML-driven anomaly detection and correction",
        description: "Sophisticated quality system applies machine learning to detect anomalies and suggest corrections. Real-time quality monitoring with predictive capabilities."
      }
    ]
  },
  {
    id: "d3",
    text: "How mature is your data governance framework?",
    dimension: "data",
    tooltip: "Evaluate the maturity of your data governance policies, compliance mechanisms, and oversight structures. Consider how formally defined your data ownership, quality standards, and usage protocols are.",
    options: [
      {
        value: 0,
        label: "No data governance framework or policies in place",
        description: "No formal governance structures exist. Data ownership, quality standards, and usage protocols are undefined and unmanaged."
      },
      {
        value: 1,
        label: "Basic policies exist with <25% compliance across organization",
        description: "Rudimentary governance policies have been created but with minimal adoption. Low awareness of requirements and limited enforcement mechanisms."
      },
      {
        value: 2,
        label: "Standard framework implemented with 50% compliance and monitoring",
        description: "Basic governance framework covers key data domains with moderate compliance. Regular audits occur but with limited consequences for non-compliance."
      },
      {
        value: 3,
        label: "Established framework with 75% compliance and regular audits",
        description: "Comprehensive governance framework with good compliance across the organization. Regular audits with formal corrective action processes."
      },
      {
        value: 4,
        label: "Comprehensive program with 90% compliance and automated controls",
        description: "Mature governance program with high compliance rates. Automated controls enforce key policies with real-time monitoring of governance metrics."
      },
      {
        value: 5,
        label: "Industry-leading governance with continuous monitoring and optimization",
        description: "Sophisticated governance framework often referenced as industry best practice. Continuous improvement with advanced monitoring and adaptability to evolving requirements."
      }
    ]
  },
  {
    id: "d4",
    text: "What is the state of your data infrastructure?",
    dimension: "data",
    tooltip: "Assess the modernity, scalability, and integration capabilities of your data storage and processing systems. Consider cloud adoption, system age, and architectural approaches.",
    options: [
      {
        value: 0,
        label: "Legacy systems only; outdated infrastructure over 5 years old",
        description: "Aging on-premises infrastructure with limited scalability. Systems are difficult to integrate and cannot support modern AI workloads."
      },
      {
        value: 1,
        label: "Mixed environment with 20% modern cloud systems; limited integration",
        description: "Predominantly legacy systems with minimal cloud adoption. Basic integration capabilities with significant performance limitations."
      },
      {
        value: 2,
        label: "Hybrid infrastructure with 50% cloud adoption and basic orchestration",
        description: "Equal mix of legacy and modern systems with partial cloud migration. Basic orchestration tools enable limited data movement between systems."
      },
      {
        value: 3,
        label: "Modern architecture with 80% cloud-native services and microservices",
        description: "Predominantly cloud-based infrastructure with modern architecture patterns. Good scalability with effective integration between key systems."
      },
      {
        value: 4,
        label: "Advanced multi-cloud with 95% cloud coverage and containerization",
        description: "Nearly full cloud adoption with containerized applications. Multi-cloud strategy with sophisticated orchestration and high scalability."
      },
      {
        value: 5,
        label: "State-of-the-art cloud-native infrastructure with serverless computing",
        description: "Cutting-edge cloud-native infrastructure leveraging serverless and managed services. Highly elastic with automated scaling and robust resilience mechanisms."
      }
    ]
  },
  {
    id: "d5",
    text: "How well is your data labeled for AI training?",
    dimension: "data",
    tooltip: "Evaluate the extent and quality of data labeling for machine learning model training. Consider both the percentage of data that is properly labeled and the processes used for ensuring label accuracy.",
    options: [
      {
        value: 0,
        label: "No systematic data labeling; <5% of data labeled accurately",
        description: "Almost no data is properly labeled for AI training. Labeling efforts, if any, are ad hoc without quality control processes."
      },
      {
        value: 1,
        label: "Basic manual labeling covering 20% of critical training data",
        description: "Limited labeling of essential datasets through manual processes. No formal quality assurance for labels with significant inconsistencies."
      },
      {
        value: 2,
        label: "Systematic labeling of 40% of data with quality controls",
        description: "Structured approach labels a moderate portion of data. Basic quality checks exist but labeling standards vary across projects."
      },
      {
        value: 3,
        label: "Comprehensive labeling (60%) with validation workflows",
        description: "Majority of relevant data has labels with established validation workflows. Consistent labeling standards applied across most datasets."
      },
      {
        value: 4,
        label: "Advanced labeling (80%) with semi-automated processes",
        description: "Most data is labeled using semi-automated tools with human validation. Comprehensive quality assurance with cross-checking mechanisms."
      },
      {
        value: 5,
        label: "Full ML-assisted labeling (95%+) with continuous validation",
        description: "Nearly all relevant data is labeled using ML-assisted techniques. Sophisticated validation ensures high accuracy with continuous improvement processes."
      }
    ]
  },
  {
    id: "d6",
    text: "What level of data security measures are in place?",
    dimension: "data",
    tooltip: "Assess the robustness of your data security controls, including encryption, access controls, and monitoring. Consider compliance with industry standards and how comprehensively security is implemented.",
    options: [
      {
        value: 0,
        label: "Basic security only with passwords and simple encryption",
        description: "Minimal security controls limited to basic password protection. Simple encryption methods with significant vulnerability to breaches."
      },
      {
        value: 1,
        label: "Standard measures including encryption and basic access controls",
        description: "Fundamental security measures with standard encryption and access restrictions. Limited monitoring with reactive security posture."
      },
      {
        value: 2,
        label: "Enhanced security with MFA, audit logs, and encryption at rest/transit",
        description: "Improved controls including multi-factor authentication and comprehensive encryption. Basic monitoring with audit trails for key systems."
      },
      {
        value: 3,
        label: "Comprehensive framework meeting ISO 27001 requirements",
        description: "Robust security framework aligned with international standards. Regular audits with formal incident response procedures and user access reviews."
      },
      {
        value: 4,
        label: "Advanced measures with Zero Trust architecture and AI monitoring",
        description: "Sophisticated security employing Zero Trust principles and AI-powered monitoring. Proactive threat detection with comprehensive defense mechanisms."
      },
      {
        value: 5,
        label: "State-of-the-art security including quantum-safe encryption",
        description: "Leading-edge security incorporating future-proof encryption technologies. Continuous validation with advanced threat intelligence and automated response systems."
      }
    ]
  },
  {
    id: "d7",
    text: "How is data privacy managed in your organization?",
    dimension: "data",
    tooltip: "Evaluate your approach to protecting personal and sensitive data, including compliance with regulations like GDPR and CCPA. Consider both technical measures and organizational processes.",
    options: [
      {
        value: 0,
        label: "Minimal privacy controls meeting basic regulatory requirements",
        description: "Basic compliance measures without comprehensive privacy strategy. Reactive approach with minimal protection beyond legal minimum requirements."
      },
      {
        value: 1,
        label: "Standard privacy policies aligned with GDPR principles",
        description: "Fundamental privacy policies following regulatory guidelines. Limited implementation of privacy-enhancing technologies and manual compliance processes."
      },
      {
        value: 2,
        label: "Active compliance program covering GDPR, CCPA, and industry standards",
        description: "Established privacy program addressing major regulations. Defined processes for data subject rights and regular compliance assessments."
      },
      {
        value: 3,
        label: "Privacy-by-design approach embedded in all data processes",
        description: "Privacy considerations integrated into data collection and processing by default. Comprehensive data mapping with regular impact assessments."
      },
      {
        value: 4,
        label: "Advanced privacy framework with automated classification",
        description: "Sophisticated privacy controls using automated data classification. Privacy enhancing technologies deployed broadly with proactive compliance monitoring."
      },
      {
        value: 5,
        label: "Leading-edge privacy-preserving computation and federated learning",
        description: "Cutting-edge approaches using privacy-preserving computation methods. Federated learning and differential privacy techniques minimize data exposure while maximizing utility."
      }
    ]
  },
  {
    id: "d8",
    text: "What is your data storage and processing capability?",
    dimension: "data",
    tooltip: "Assess the scale and sophistication of your data storage and processing infrastructure. Consider total data volume handled, processing speed, and advanced capabilities like real-time processing.",
    options: [
      {
        value: 0,
        label: "Basic storage infrastructure managing <1TB of data",
        description: "Limited storage capacity handling small data volumes. Processing capabilities restricted to basic batch operations with significant latency."
      },
      {
        value: 1,
        label: "Standard solutions handling up to 10TB with basic analytics",
        description: "Conventional storage infrastructure managing moderate data volumes. Processing limited to scheduled batch operations with simple analytics capabilities."
      },
      {
        value: 2,
        label: "Scalable architecture managing up to 100TB with distributed processing",
        description: "Expanded capacity handling larger datasets with some distributed processing capabilities. Mix of batch and limited near-real-time analytics."
      },
      {
        value: 3,
        label: "Enterprise-grade systems handling up to 1PB with advanced analytics",
        description: "Robust infrastructure managing petabyte-scale data. Sophisticated analytics with capability for both batch and streaming workloads."
      },
      {
        value: 4,
        label: "Advanced cloud infrastructure managing up to 10PB with real-time processing",
        description: "High-performance cloud platforms supporting multi-petabyte scale. Full real-time processing capabilities with advanced analytics and ML workloads."
      },
      {
        value: 5,
        label: "Hyperscale distributed systems managing >10PB with edge computing",
        description: "State-of-the-art infrastructure handling massive data volumes across distributed locations. Seamless integration of cloud and edge computing with real-time processing."
      }
    ]
  },
  {
    id: "d9",
    text: "How well are your data integration processes automated?",
    dimension: "data",
    tooltip: "Evaluate the level of automation in your data integration workflows. Consider the percentage of integration processes that are automated versus manual, and the sophistication of your automation tools.",
    options: [
      {
        value: 0,
        label: "Manual data integration processes with no automation",
        description: "All data integration requires manual intervention. Engineers manually extract, transform, and load data with scripts or direct database operations."
      },
      {
        value: 1,
        label: "Basic automation covering 20% of data integration workflows",
        description: "Limited automation for routine integration tasks. Simple scheduled jobs handle a small portion of data movement with manual oversight required."
      },
      {
        value: 2,
        label: "Partial automation of 40% of integration processes",
        description: "Less than half of integration workflows are automated. Basic ETL tools in place but significant manual intervention still needed for most processes."
      },
      {
        value: 3,
        label: "Significant automation covering 60% of data pipelines",
        description: "Majority of standard data pipelines operate with minimal intervention. Modern ETL/ELT tools handle routine integration with exceptions managed manually."
      },
      {
        value: 4,
        label: "Advanced automation of 80% of integration workflows",
        description: "Most integration processes run autonomously with self-healing capabilities. Sophisticated orchestration tools manage dependencies and error handling."
      },
      {
        value: 5,
        label: "Full AI-driven automation of 95%+ of data processes",
        description: "Nearly complete automation with AI optimization of integration processes. Self-adapting workflows with predictive maintenance and automatic resource optimization."
      }
    ]
  },
  {
    id: "d10",
    text: "What percentage of your data pipelines are fully automated?",
    dimension: "data",
    tooltip: "Assess the extent of end-to-end automation in your data pipelines. Consider the percentage of data flows that operate without manual intervention, and include factors like monitoring and error handling.",
    options: [
      {
        value: 0,
        label: "No automated data pipelines (0% automation)",
        description: "All data movement and transformation requires manual processes. No established pipelines or automation framework exists."
      },
      {
        value: 1,
        label: "Initial automation of critical pipelines (10% automated)",
        description: "A few essential data flows have basic automation. Majority require manual triggering and monitoring with frequent intervention."
      },
      {
        value: 2,
        label: "Growing automation coverage (25% of pipelines)",
        description: "Quarter of data pipelines operate with basic automation. Limited monitoring capabilities with manual intervention needed for exceptions and errors."
      },
      {
        value: 3,
        label: "Significant automation (50% of data flows)",
        description: "Half of all data pipelines function with reliable automation. Standard error handling with alerts for human intervention when needed."
      },
      {
        value: 4,
        label: "Advanced automation (75% of pipelines)",
        description: "Three-quarters of pipelines operate autonomously. Sophisticated monitoring with predictive alerts and automated recovery for common failures."
      },
      {
        value: 5,
        label: "Comprehensive automation (>90% with monitoring)",
        description: "Nearly all pipelines operate without regular human intervention. End-to-end automation includes monitoring, error handling, recovery, and optimization."
      }
    ]
  }
];
